import { Box, Typography, Checkbox } from 'src/components';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { faUsers, faCalendarUsers } from '@fortawesome/pro-solid-svg-icons';
import {
  faUsers as faUsersRegular,
  faCalendarUsers as faCalendarUsersRegular,
} from '@fortawesome/pro-regular-svg-icons';

interface VolumeTypeBoxProps {
  type: VolumeType;
  selected: boolean;
  title: string;
  description: string;
  showNewBadge?: boolean;
  onChange: (type: VolumeType) => void;
  disabled?: boolean;
}

type VolumeType = 'totalClaims' | 'encounters';

const VolumeTypeBox: React.FC<VolumeTypeBoxProps> = ({
  type,
  selected,
  title,
  description,
  showNewBadge = false,
  onChange,
  disabled = false,
}) => {
  const selectedStyles = 'bg-sky-50 outline-sky-600';
  const unselectedStyles = 'outline-neutral-300';

  const iconColor = {
    selected: '#207FC5',
    unselected: '#737373',
  };

  const icon = {
    totalClaims: {
      selected: faUsers,
      unselected: faUsersRegular,
    },
    encounters: {
      selected: faCalendarUsers,
      unselected: faCalendarUsersRegular,
    },
  };

  const displayIcon = selected ? icon[type].selected : icon[type].unselected;

  const handleBoxClick = () => {
    onChange(type);
  };

  const handleCheckboxClick = () => {
    onChange(type);
  };

  return (
    <Box className="relative">
      {disabled && (
        <Box className="absolute -top-3 left-1/2 -translate-x-1/2 h-6 px-3 bg-neutral-400 rounded-3xl inline-flex justify-center items-center gap-2 overflow-hidden z-10">
          <Typography className="text-center justify-start text-neutral-700 text-xs font-semibold leading-none whitespace-nowrap">
            Coming Soon!
          </Typography>
        </Box>
      )}
      {showNewBadge && (
        <Box className="absolute -top-3 left-1/2 -translate-x-1/2 h-6 px-3 bg-green-600 rounded-3xl inline-flex justify-center items-center gap-2 overflow-hidden z-10">
          <Typography className="text-center justify-start text-white text-xs font-semibold leading-none">
            New!
          </Typography>
        </Box>
      )}
      <Box
        className={`self-stretch h-48 w-full max-w-44 relative px-5 py-9 rounded-lg outline outline-2 cursor-pointer ${
          selected ? selectedStyles : unselectedStyles
        }
        ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
        `}
        onClick={disabled ? undefined : handleBoxClick}
        data-testid={`volume-type-box-${type}`}
      >
        <Checkbox
          checked={selected}
          onChange={handleCheckboxClick}
          className="absolute top-4 right-5"
        />
        <Box className="flex flex-col gap-2 items-center justify-center">
          <Icon
            icon={displayIcon}
            color={selected ? iconColor.selected : iconColor.unselected}
            size="2xl"
          />
          <Typography className="self-stretch text-center justify-start text-primary text-sm font-bold leading-tight">
            {title}
          </Typography>
          <Typography className="self-stretch text-center justify-start text-secondary text-xs font-normal leading-none">
            {description}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default VolumeTypeBox;
